import { Component } from 'react';

/**
 * Captura errores de render en cualquier subtree y muestra un fallback amigable
 * en lugar de pantalla en blanco. Loguea el error en consola sólo en dev.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary atrapó:', error, errorInfo);
    }
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV !== 'production';
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50 p-6"
        >
          <div className="max-w-xl w-full bg-white rounded-xl shadow-lg border border-rose-100 p-8 text-center space-y-5">
            <div className="mx-auto w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-rose-600">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Algo salió mal</h1>
              <p className="text-slate-600 mt-2">
                Ocurrió un error inesperado al cargar esta sección. Tu sesión sigue activa.
              </p>
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 rounded-lg bg-rose-900 text-white font-medium hover:bg-rose-800 transition-colors"
                data-testid="error-boundary-reload"
              >
                Recargar página
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-5 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                data-testid="error-boundary-home"
              >
                Ir al inicio
              </button>
            </div>
            {isDev && this.state.error && (
              <details className="text-left bg-slate-50 rounded-md p-3 text-xs text-slate-700 max-h-48 overflow-auto">
                <summary className="cursor-pointer font-medium text-slate-800 mb-2">
                  Detalle técnico (solo visible en desarrollo)
                </summary>
                <pre className="whitespace-pre-wrap break-words">
                  {String(this.state.error?.toString() || this.state.error)}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <p className="text-xs text-slate-500">
              Si el problema persiste, contacta al administrador del sistema.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
